import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

import type {
	OptionsVisibilityValidator,
	PropertyTypeValidator,
} from '../../../../../../../../../common/types';
import type { ViewType } from '../../../../../../../../../common/types/context';
import type {
	Field,
	FieldId,
	FieldPublicShape,
	FieldType,
} from '../../../../../../../../../common/types/field';
import type { FieldInternalMetadata } from '../../../../../../../../../common/types/field-internal-metadata';
import { getSupportedFieldConfiguration } from '../../../../../../../../../controllers/view-configuration';
import { VALID_RESULT } from './constants';
import type { FieldValidationResult, PropertyValidationResult } from './types';
import { filterInvalidResults, isFieldPropertyName, prepareValidationResult } from './utils';

type ValidateFieldChangesArgs = {
	fieldType: FieldType;
	fieldId: FieldId;
	fieldFromIframe: FieldPublicShape;
	viewType: ViewType;
	fieldMetadata: FieldInternalMetadata;
	currentField: Field;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
};
export const validateFieldChanges = async ({
	fieldType,
	fieldId,
	currentField,
	fieldFromIframe,
	fieldMetadata,
	viewType,
	createAnalyticsEvent,
}: ValidateFieldChangesArgs): Promise<FieldValidationResult> => {
	const fieldConfiguration = getSupportedFieldConfiguration(viewType, fieldType);
	const fieldValidators = fieldConfiguration?.publicShapeValidators;

	if (!fieldFromIframe || !fieldValidators) {
		return VALID_RESULT;
	}

	const validatorsArray = Object.entries<PropertyTypeValidator | OptionsVisibilityValidator>(
		fieldValidators,
	);

	const fieldValidationResults: PropertyValidationResult[] = await Promise.all(
		validatorsArray.map(async ([propertyName, validate]) => {
			if (isFieldPropertyName(fieldFromIframe, propertyName)) {
				const propertyValue = fieldFromIframe[propertyName];
				const validationResult = await validate(
					propertyValue,
					fieldMetadata,
					createAnalyticsEvent,
					{
						currentField,
						viewType,
					},
				);

				return prepareValidationResult({
					validationResult,
					fieldId,
					fieldType,
					propertyName,
				});
			}
			return VALID_RESULT;
		}),
	);

	const invalidValidationResult = filterInvalidResults(fieldValidationResults);

	if (invalidValidationResult.length > 0) {
		return {
			isValid: false,
			errors: invalidValidationResult.map((result) => result.error),
		};
	}
	return VALID_RESULT;
};
